import React, { useRef, useState, useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Button, Input, CircularProgress, Checkbox } from "@nextui-org/react";
import { FiSearch, FiChevronDown, FiChevronUp, FiList } from "react-icons/fi";
import { MdOutlineStarBorder } from "react-icons/md";

import { getGradeBackgroundColor } from "../../utils/colorUtils";
import CustomCopyToClipboard from "../../components/Kit/CustomCopyToClipboard";
import myAxios from "../../utils/axiosWrapper";
import getConfig from "../../conf/config";

const CallSearchModal = ({ existingCallIds = [], onAddCalls, onClose }) => {
  const { t } = useTranslation();

  // Tab state: "search" or "list"
  const [activeTab, setActiveTab] = useState("search");

  // Search state
  const [searchText, setSearchText] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [selectedCallIds, setSelectedCallIds] = useState([]);
  const [adding, setAdding] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const lastSearchTextRef = useRef("");
  const debounceRef = useRef(null);

  // Expand/collapse state for call summaries
  const [expandedCallId, setExpandedCallId] = useState(null);

  // List import state
  const [lists, setLists] = useState([]);
  const [loadingLists, setLoadingLists] = useState(false);
  const [selectedListId, setSelectedListId] = useState(null);
  const [listCalls, setListCalls] = useState([]);
  const [loadingListCalls, setLoadingListCalls] = useState(false);

  const fetchCalls = async (text, page = 0) => {
    const response = await myAxios.post(`${getConfig().API_URL}/audio/api/v1/calls/algolia_calls`, {
      searchText: text,
      page,
      hitCount: 15,
    });
    return {
      calls: response?.data?.calls || [],
      totalPages: response?.data?.totalPages || 0,
    };
  };

  const executeSearch = async (text) => {
    if (!text.trim()) {
      setResults([]);
      setHasSearched(false);
      return;
    }
    setSearching(true);
    setHasSearched(true);
    setCurrentPage(0);
    lastSearchTextRef.current = text;
    try {
      const data = await fetchCalls(text, 0);
      const filtered = data.calls.filter((call) => !existingCallIds.includes(call.id));
      setResults(filtered);
      setTotalPages(data.totalPages);
    } catch (error) {
      console.error("Error searching calls:", error);
    }
    setSearching(false);
  };

  // Debounced auto-search on type
  useEffect(() => {
    if (activeTab !== "search") return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      executeSearch(searchText);
    }, 400);
    return () => clearTimeout(debounceRef.current);
  }, [searchText, activeTab]);

  // Fetch lists when switching to list tab
  useEffect(() => {
    if (activeTab === "list" && lists.length === 0 && !loadingLists) {
      // eslint-disable-next-line no-use-before-define
      loadLists();
    }
  }, [activeTab]);

  const loadLists = async () => {
    setLoadingLists(true);
    try {
      const response = await myAxios.get(`${getConfig().API_URL}/audio/api/v1/lists`);
      const retrievedLists = response?.data?.[0]?.lists || [];
      const activeLists = retrievedLists.filter((l) => !l.is_deleted && !l.is_archived);
      setLists(activeLists);
    } catch (error) {
      console.error("Error fetching lists:", error);
    }
    setLoadingLists(false);
  };

  const handleSelectList = async (listId) => {
    setSelectedListId(listId);
    setLoadingListCalls(true);
    setSelectedCallIds([]);
    try {
      const response = await myAxios.get(`${getConfig().API_URL}/audio/api/v1/lists/${listId}`);
      const listData = response?.data?.[0]?.list || null;
      const calls = listData?.calls || [];
      const filtered = calls.filter((call) => !existingCallIds.includes(call.id));
      setListCalls(filtered);
    } catch (error) {
      console.error("Error fetching list data:", error);
    }
    setLoadingListCalls(false);
  };

  const handleLoadMore = async () => {
    const nextPage = currentPage + 1;
    if (nextPage >= totalPages || loadingMore) return;
    setLoadingMore(true);
    setCurrentPage(nextPage);
    try {
      const data = await fetchCalls(lastSearchTextRef.current, nextPage);
      const filtered = data.calls.filter((call) => !existingCallIds.includes(call.id));
      setResults((prev) => [...prev, ...filtered]);
    } catch (error) {
      console.error("Error loading more calls:", error);
    }
    setLoadingMore(false);
  };

  const handleScroll = useCallback(
    (e) => {
      const { scrollTop, scrollHeight, clientHeight } = e.target;
      if (scrollHeight - scrollTop - clientHeight < 100) {
        handleLoadMore();
      }
    },
    [currentPage, totalPages, loadingMore]
  );

  const toggleSelection = (callId) => {
    setSelectedCallIds((prev) =>
      prev.includes(callId) ? prev.filter((id) => id !== callId) : [...prev, callId]
    );
  };

  const handleSelectAll = () => {
    const availableCalls = activeTab === "search" ? results : listCalls;
    const allIds = availableCalls.map((c) => c.id);
    const allSelected = allIds.every((id) => selectedCallIds.includes(id));
    if (allSelected) {
      setSelectedCallIds((prev) => prev.filter((id) => !allIds.includes(id)));
    } else {
      setSelectedCallIds((prev) => [...new Set([...prev, ...allIds])]);
    }
  };

  const handleAdd = async () => {
    if (selectedCallIds.length === 0) return;
    setAdding(true);
    await onAddCalls(selectedCallIds);
    setAdding(false);
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return "-";
    const d = new Date(timestamp * 1000);
    const date = d.toLocaleDateString("fr-FR", {
      day: "2-digit",
      month: "2-digit",
      year: "2-digit",
    });
    const time = d.toLocaleTimeString("fr-FR", {
      hour: "2-digit",
      minute: "2-digit",
    });
    return `${date} ${time}`;
  };

  const hasMore = currentPage + 1 < totalPages;

  const toggleExpand = (callId) => {
    setExpandedCallId((prev) => (prev === callId ? null : callId));
  };

  const renderCallRow = (call) => {
    const callId = call.id;
    const grade = call?.analysis?.call_overall_grade;
    const gradeNormalized = grade >= 0 ? Math.round((grade + Number.EPSILON) * 10) / 10 : -1;
    const gradeBgColor = getGradeBackgroundColor(gradeNormalized);
    const isSelected = selectedCallIds.includes(callId);
    const shortId = callId ? callId.substring(0, 7) : "";
    const isExpanded = expandedCallId === callId;
    const summary = call?.analysis?.overview || call?.analysis?.call_summary;
    const chapters = call?.analysis?.chapters;
    const hasDetails = summary || (chapters && chapters.length > 0);

    return (
      <div
        key={callId}
        className={`rounded-xl px-4 py-3 transition-all ${
          isSelected
            ? "bg-primary-100/60 ring-2 ring-primary-300"
            : "bg-primary-50/30 hover:bg-primary-50/60"
        }`}
      >
        {/* Top row: checkbox, ID, operator, date, grade, chevron */}
        <div className="flex items-center gap-3">
          <Checkbox
            isSelected={isSelected}
            onValueChange={() => toggleSelection(callId)}
            size="sm"
          />

          {/* Call ID - copy to clipboard */}
          <CustomCopyToClipboard
            displayText={`#${shortId}`}
            copyText={callId}
            displayClassName="text-xs font-mono text-gray-500"
            wrapperClassName="!p-1 !rounded-md"
          />

          {/* Operator name */}
          <span className="text-sm font-medium text-gray-700 truncate max-w-[140px]">
            {call.operator_name || "-"}
          </span>

          {/* Date */}
          <span className="text-xs text-gray-500 shrink-0">{formatDate(call.call_timestamp)}</span>

          {/* Spacer */}
          <div className="flex-1" />

          {/* Grade */}
          <div
            className={`flex items-center font-semibold px-1.5 py-0.5 rounded text-xs ${
              gradeNormalized >= 0 ? gradeBgColor : "bg-gray-100 text-gray-600"
            } shrink-0`}
          >
            <MdOutlineStarBorder size={12} />
            <span className="ml-0.5">{gradeNormalized >= 0 ? gradeNormalized : "N.A"}</span>
          </div>

          {/* Expand toggle */}
          {hasDetails && (
            <button
              type="button"
              className="p-1 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors shrink-0"
              onClick={(e) => {
                e.stopPropagation();
                toggleExpand(callId);
              }}
            >
              {isExpanded ? <FiChevronUp size={14} /> : <FiChevronDown size={14} />}
            </button>
          )}
        </div>

        {/* Details (expanded): summary + chapters */}
        {isExpanded && hasDetails && (
          <div className="mt-2 ml-9 pt-2 border-t border-gray-200/60">
            {summary && (
              <div className="mb-2">
                <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1">
                  {t("Résumé")}
                </p>
                <p className="text-xs text-gray-500 leading-relaxed">{summary}</p>
              </div>
            )}
            {chapters && chapters.length > 0 && (
              <div>
                <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1">
                  {t("Chapitres")}
                </p>
                {chapters.map((chapter, chapterIdx) => (
                  <div
                    key={`chapter-${chapterIdx}`}
                    className="mb-2"
                  >
                    {chapter?.title && (
                      <p className="text-xs font-medium text-gray-600 mb-0.5">{chapter.title}</p>
                    )}
                    {chapter?.points?.map((point, pointIdx) => (
                      <div
                        key={`point-${chapterIdx}-${pointIdx}`}
                        className="flex items-start text-xs text-gray-500 ml-2 mb-0.5"
                      >
                        <span className="mr-1.5 mt-0.5 shrink-0">&#8226;</span>
                        <span>{point}</span>
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Audio player */}
        {call.audio_url && (
          <div className="mt-2 ml-9">
            <audio
              controls
              preload="none"
              className="w-full h-8"
              style={{ minWidth: 0 }}
              onClick={(e) => e.stopPropagation()}
            >
              <source src={call.audio_url} />
              <track kind="captions" />
            </audio>
          </div>
        )}
      </div>
    );
  };

  const currentCalls = activeTab === "search" ? results : listCalls;
  const allCurrentIds = currentCalls.map((c) => c.id);
  const allSelected =
    allCurrentIds.length > 0 && allCurrentIds.every((id) => selectedCallIds.includes(id));

  return (
    <div className="p-6">
      <h2 className="text-xl font-bold text-gray-900 mb-1">
        {activeTab === "search" ? t("Rechercher des appels") : t("Importer depuis une liste")}
      </h2>
      <p className="text-sm text-gray-400 mb-5">
        {activeTab === "search"
          ? t("Recherchez et sélectionnez les appels à ajouter au debrief")
          : t("Sélectionnez une liste puis choisissez les appels à importer")}
      </p>

      {/* Tabs */}
      <div className="flex gap-1 mb-5 bg-gray-100 rounded-lg p-1">
        <button
          type="button"
          className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
            activeTab === "search"
              ? "bg-white text-gray-900 shadow-sm"
              : "text-gray-500 hover:text-gray-700"
          }`}
          onClick={() => {
            setActiveTab("search");
            setSelectedCallIds([]);
            setExpandedCallId(null);
          }}
        >
          <FiSearch size={14} />
          {t("Rechercher")}
        </button>
        <button
          type="button"
          className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
            activeTab === "list"
              ? "bg-white text-gray-900 shadow-sm"
              : "text-gray-500 hover:text-gray-700"
          }`}
          onClick={() => {
            setActiveTab("list");
            setSelectedCallIds([]);
            setExpandedCallId(null);
          }}
        >
          <FiList size={14} />
          {t("Importer depuis une liste")}
        </button>
      </div>

      {/* Search tab content */}
      {activeTab === "search" && (
        <>
          {/* Search bar */}
          <div className="mb-5">
            <Input
              placeholder={t("Rechercher par nom, résumé, entreprise...")}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              startContent={<FiSearch className="text-gray-400" />}
              size="sm"
              isClearable
              onClear={() => setSearchText("")}
            />
          </div>

          {/* Results */}
          <div
            className="max-h-[400px] overflow-auto rounded-xl p-1 -m-1"
            onScroll={handleScroll}
          >
            {searching ? (
              <div className="flex items-center justify-center py-12">
                <CircularProgress
                  size="lg"
                  color="primary"
                />
              </div>
            ) : results.length === 0 ? (
              <div className="text-center text-gray-400 py-12 text-sm">
                {hasSearched
                  ? t("Aucun appel trouvé")
                  : t("Tapez un terme de recherche pour trouver des appels")}
              </div>
            ) : (
              <div className="space-y-1.5">
                {results.map((call) => renderCallRow(call))}
                {loadingMore && (
                  <div className="flex items-center justify-center py-4">
                    <CircularProgress
                      size="sm"
                      color="primary"
                    />
                  </div>
                )}
                {!loadingMore && hasMore && (
                  <div className="text-center text-xs text-gray-400 py-2">
                    {t("Scroll pour charger plus")}
                  </div>
                )}
              </div>
            )}
          </div>
        </>
      )}

      {/* List tab content */}
      {activeTab === "list" && (
        <div className="max-h-[450px] overflow-auto rounded-xl p-1 -m-1">
          {!selectedListId ? (
            // List selection
            loadingLists ? (
              <div className="flex items-center justify-center py-12">
                <CircularProgress
                  size="lg"
                  color="primary"
                />
              </div>
            ) : lists.length === 0 ? (
              <div className="text-center text-gray-400 py-12 text-sm">
                {t("Aucune liste disponible")}
              </div>
            ) : (
              <div className="space-y-2">
                {lists.map((list) => (
                  <button
                    key={list.id}
                    type="button"
                    className="w-full text-left rounded-xl px-4 py-3 bg-primary-50/30 hover:bg-primary-50/60 transition-all"
                    onClick={() => handleSelectList(list.id)}
                  >
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-gray-700">{list.name}</span>
                      <span className="text-xs text-gray-400">
                        {list.nb_calls || 0} {t("appels")}
                      </span>
                    </div>
                  </button>
                ))}
              </div>
            )
          ) : (
            // List calls
            <>
              <button
                type="button"
                className="flex items-center gap-1 text-sm text-primary hover:text-primary-600 mb-3 transition-colors"
                onClick={() => {
                  setSelectedListId(null);
                  setListCalls([]);
                  setSelectedCallIds([]);
                  setExpandedCallId(null);
                }}
              >
                <FiChevronUp
                  size={14}
                  className="rotate-[-90deg]"
                />
                {t("Retour aux listes")}
              </button>

              {loadingListCalls ? (
                <div className="flex items-center justify-center py-12">
                  <CircularProgress
                    size="lg"
                    color="primary"
                  />
                </div>
              ) : listCalls.length === 0 ? (
                <div className="text-center text-gray-400 py-12 text-sm">
                  {t("Aucun nouvel appel dans cette liste")}
                </div>
              ) : (
                <div className="space-y-1.5">
                  {/* Select all */}
                  <div className="flex items-center gap-2 px-4 py-2">
                    <Checkbox
                      isSelected={allSelected}
                      onValueChange={handleSelectAll}
                      size="sm"
                    />
                    <span className="text-xs text-gray-500">
                      {t("Tout sélectionner")} ({listCalls.length})
                    </span>
                  </div>
                  {listCalls.map((call) => renderCallRow(call))}
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* Actions */}
      <div className="flex items-center justify-between mt-5 pt-4 border-t border-gray-100">
        <span className="text-sm text-gray-500">
          {selectedCallIds.length > 0 ? `${selectedCallIds.length} ${t("sélectionné(s)")}` : ""}
        </span>
        <div className="flex gap-2">
          <Button
            variant="light"
            size="sm"
            onClick={onClose}
          >
            {t("Annuler")}
          </Button>
          <Button
            color="primary"
            size="sm"
            onClick={handleAdd}
            isLoading={adding}
            isDisabled={selectedCallIds.length === 0}
          >
            {t("Ajouter")}
            {selectedCallIds.length > 0 && ` (${selectedCallIds.length})`}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default CallSearchModal;
